import { z } from 'zod';

import { defineQuery } from '@nesso/common-contracts/define';
import { practiceIdSchema, practiceMemberIdSchema } from '@nesso/common-contracts/ids';
import { metadata } from '@nesso/common-contracts/schema-metadata';

import { clientIdSchema, sessionIdSchema } from './ids';
import { sessionSchema } from './session.models';
import {
    generationStatusSchema,
    sessionNoteSourceSchema,
    sessionNoteStatusSchema,
    sessionNoteTypeSchema,
} from './session-note.models';

export const GET_SESSION_BY_ID_TYPE = 'session/get-by-id';
export const GET_SESSION_LIST_TYPE = 'session/get-list';
export const GET_CLIENT_ACTIVITY_TYPE = 'session/get-client-activity';
export const GET_CLIENT_SERVICE_CODES_TYPE = 'session/get-client-service-codes';

export const getSessionById = defineQuery(
    GET_SESSION_BY_ID_TYPE,
    z.object({
        id: sessionIdSchema,
        practiceMemberId: practiceMemberIdSchema,
    }),
    sessionSchema,
);

export const sessionListItemSchema = sessionSchema
    .pick({
        id: true,
        date: true,
        duration: true,
        clients: true,
        actions: true,
        treatmentApproaches: true,
    })
    .extend({
        isDayCompleted: z.boolean(),
        currentNote: z
            .object({
                id: z.uuid(),
                type: sessionNoteTypeSchema,
                status: sessionNoteStatusSchema,
                source: sessionNoteSourceSchema,
                generator: z
                    .object({
                        status: generationStatusSchema,
                        errorType: z.string().nullable(),
                    })
                    .nullable(),
            })
            .nullable(),
    });

export type SessionListItem = z.infer<typeof sessionListItemSchema>;

export type SessionListItemRecord = z.input<typeof sessionListItemSchema>;

export const sortOrders = ['asc', 'desc'] as const;

export const getSessionList = defineQuery(
    GET_SESSION_LIST_TYPE,
    z.object({
        practiceId: practiceIdSchema,
        practiceMemberId: practiceMemberIdSchema,
        startDate: z.iso.datetime().optional(),
        endDate: z.iso.datetime().optional(),
        status: sessionNoteStatusSchema.optional(),
        clientId: clientIdSchema.optional(),
        order: z.enum(sortOrders).default('desc'),
        search: z.string().trim().optional(),
        excludeFirstGeneration: z.boolean().optional().default(false),
        pageSize: z.number().optional(),
        cursor: z.string().nullable().optional(),
    }),
    z.object({
        items: z.array(sessionListItemSchema),
        nextCursor: z.string().nullable(),
    }),
);

export const clientActivityItemSchema = sessionSchema
    .pick({ id: true, date: true, duration: true, status: true })
    .extend({ hasNote: z.boolean() });

export type ClientActivityItem = z.infer<typeof clientActivityItemSchema>;

export type ClientActivityItemRecord = z.input<typeof clientActivityItemSchema>;

export const getClientActivity = defineQuery(
    GET_CLIENT_ACTIVITY_TYPE,
    z.object({
        clientId: clientIdSchema.meta(metadata.recordId),
        practiceId: practiceIdSchema.meta(metadata.nonSensitive),
        practiceMemberId: practiceMemberIdSchema.meta(metadata.nonSensitive),
    }),
    z.object({
        completedCount: z.number().int(),
        cancelledCount: z.number().int(),
        recent: z.array(clientActivityItemSchema),
        nextScheduledDate: z.iso.datetime().nullable(),
    }),
);

export const getClientServiceCodes = defineQuery(
    GET_CLIENT_SERVICE_CODES_TYPE,
    z.object({
        clientId: clientIdSchema.meta(metadata.recordId),
        practiceId: practiceIdSchema.meta(metadata.nonSensitive),
        practiceMemberId: practiceMemberIdSchema.meta(metadata.nonSensitive),
    }),
    z.object({
        serviceCodes: z.array(z.string()),
    }),
);
